import React, { useCallback, useEffect } from 'react';

import {
  Flex,
  Text,
  HStack,
  Stack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Switch,
} from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useToggle } from 'react-use';

import {
  Button, Checkbox, Icon, Input,
} from 'components';

export default function Checklists({
  index, allowEdition, execution, isInactive, forceParentRerender,
}) {
  const {
    register, watch, control, setValue,
  } = useFormContext();
  const [, forceReRender] = useToggle(false);
  const inputFields = watch(`fields.${index}.inputs`)?.filter((input) => {
    if (allowEdition) {
      return input;
    }
    return input.active;
  });

  const optionFields = watch(`fields.${index}.options`) as GenericObject[];

  const { append, remove } = useFieldArray({
    control,
    name: `fields.${index}.inputs`,
  });

  useEffect(() => {
    if (isInactive) {
      inputFields?.forEach((_, indexOption) => {
        setValue(`fields.${index}.inputs.${indexOption}.active`, false);
      });
      return forceReRender();
    }
    return null;
  }, [isInactive]);

  const hasOneInputActive = Boolean(inputFields?.find((_, indexField) => watch(`fields.${index}.inputs.${indexField}.active`)));

  useEffect(() => {
    setValue(`fields.${index}.active`, hasOneInputActive);
    return forceParentRerender();
  }, [hasOneInputActive]);

  const Row = useCallback(({ active, label, i }) => {
    const [isEditing, toggle] = useToggle(false);

    return (
      <Tr>
        <Td>
          <Flex alignItems="center">
            {allowEdition && (
            <Switch
              {...register(`fields.${index}.inputs.${i}.active`)}
              defaultChecked={active}
              size="sm"
            />
            )}
            <Text
              mx={4}
              hidden={isEditing}
              variant="smallSemiBold">
              {label}
            </Text>
            <Input
              mx={4}
              height="20px"
              width="min"
              viewProps={{
                width: 'min',
                hidden: !isEditing,
                alignSelf: 'center',
              }}
              defaultValue={label}
              {...register(`fields.${index}.inputs.${i}.label`)}
            />
            {allowEdition && (
            <Icon
              name={isEditing ? 'check' : 'edit'}
              onClick={() => {
                toggle();
                if (isEditing) {
                  forceReRender();
                }
              }}
            />
            )}
          </Flex>
        </Td>
        {optionFields?.map((option, idx) => (
          <Td
            width="20%"
            key={`fields.${index}.inputs.${i}.option.${idx}`}>
            <Flex>
              <Checkbox
                isChecked={option === execution?.fields[i]?.checked}
                isDisabled
              />
            </Flex>
          </Td>
        ))}
        <Td hidden={!allowEdition}>
          <Flex justify="flex-end">
            <Icon
              onClick={() => remove(i)}
              name="trash-alt"
              color="gray.500"
            />
          </Flex>
        </Td>
      </Tr>
    );
  }, [index, allowEdition, forceReRender, remove, register, optionFields, execution]);
  const Head = useCallback(({ option, i }) => {
    const [isEditing, toggle] = useToggle(false);

    return (
      <Th>
        <HStack>
          <Text
            hidden={isEditing}
            variant="smallSemiBold">
            {option}
          </Text>
          <Input
            border="none"
            height="20px"
            viewProps={{
              border: 'none',
              hidden: !isEditing,
            }}
            defaultValue={option}
            {...register(`fields.${index}.options.${i}`)}
          />
          {allowEdition && (
          <Icon
            name={isEditing ? 'check' : 'edit'}
            onClick={() => {
              toggle();
              if (isEditing) {
                forceReRender();
              }
            }}
          />
          )}
        </HStack>
      </Th>
    );
  }, [index, allowEdition, forceReRender, register]);
  return (
    <Stack>
      <Table
        size="sm"
        overflow="hidden"
        borderRadius={8}>
        <Thead bg="gray.300">
          <Tr>
            <Th>Item</Th>
            {optionFields?.map((option, i) => (
              <Head
                key={`checklist-head-${i}`}
                option={option}
                i={i}
              />
            ))}
            <Th hidden={!allowEdition}>Ações</Th>
          </Tr>
        </Thead>
        <Tbody bg="white.default">
          {inputFields?.map((item, i) => (
            <Row
              key={`checklist-row-${i}`}
              i={i}
              {...item}
            />
          ))}
        </Tbody>
      </Table>
      <Button
        hidden={!allowEdition}
        alignSelf="flex-start"
        variant="ghost.blue"
        onClick={() => append({ active: false, label: 'Novo Item' })}>
        <Icon name="plus" />
        <Text
          variant="mediumBold"
          ml={2}>
          Adicionar Item
        </Text>
      </Button>
    </Stack>
  );
}
